import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner, Button, Checkbox, ckConfirm, Dialog, Field, SimpleTable, Spinner, TextArea, TextInput, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import { yemotOutboundApi, type YemotSendPreview, type YemotSetup } from "../../api/salesApi";
import { errorMessage, fmtNum } from "./salesUi";

/**
 * Yemot HaMashiach outbound from the leads (Turbo parity — kupa «שיגור» / pbx «התקשר»):
 * a tzintuk or a voice message (TTS) to the selected leads, and click-to-call from a lead.
 */

let setupCache: Promise<YemotSetup> | null = null;

/** is the line connected + the user's own phone (one request per page load; refreshed after a call saves a phone) */
export function useYemotSetup(enabled = true) {
  const [setup, setSetup] = useState<YemotSetup | null>(null);
  const reload = useCallback(() => {
    setupCache = yemotOutboundApi.setup().catch(() => ({ configured: false, myPhone: null, callerId: null }));
    void setupCache.then(setSetup);
  }, []);
  useEffect(() => {
    if (!enabled) return;
    if (!setupCache) setupCache = yemotOutboundApi.setup().catch(() => ({ configured: false, myPhone: null, callerId: null }));
    let alive = true;
    void setupCache.then((s) => alive && setSetup(s));
    return () => {
      alive = false;
    };
  }, [enabled]);
  return { setup, reload };
}

export type YemotSendKindUi = "tzintuk" | "voice";
const KIND_LABEL: Record<YemotSendKindUi, string> = { tzintuk: "צינתוק", voice: "הודעה קולית" };

/** «צינתוק» / «הודעה קולית» to leads: preview (count, left out, units) → confirm → send */
export function YemotSendDialog({ kind, ids, onClose, onDone }: { kind: YemotSendKindUi | null; ids: string[]; onClose: () => void; onDone?: () => void }) {
  const toast = useCkToast();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<YemotSendPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPreview(null);
    setError(null);
    setTitle("");
    setText("");
    if (!kind || !ids.length) return;
    let alive = true;
    yemotOutboundApi
      .preview({ kind, leadIds: ids })
      .then((p) => alive && setPreview(p))
      .catch((e) => alive && setError(errorMessage(e, "לא הצלחנו להכין את השיגור")));
    return () => {
      alive = false;
    };
  }, [kind, ids]);

  const tooMany = Boolean(preview && preview.count > preview.max);
  const submit = async () => {
    if (!kind || !preview) return;
    if (kind === "voice" && !text.trim()) {
      toast.error("כתבו את הטקסט להקראה");
      return;
    }
    const ok = await ckConfirm({
      title: "אישור שיגור",
      message: `לשגר ${KIND_LABEL[kind]} ל־${fmtNum(preview.count)} נמענים? הפעולה מחייגת מהקו של ימות ואינה ניתנת לביטול אחרי היציאה.`,
      confirmLabel: "שגר עכשיו",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await yemotOutboundApi.send({ kind, leadIds: ids, title: title.trim() || null, text: kind === "voice" ? text.trim() : null });
      toast(r.message, { tone: "success", action: { label: "להיסטוריית השיגורים", onClick: () => navigate("/sales/sends") } });
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(errorMessage(e, "השיגור נכשל"));
    } finally {
      setBusy(false);
    }
  };

  const s = preview?.skipped;
  const left = s ? [s.noPhone ? `${fmtNum(s.noPhone)} בלי טלפון` : null, s.duplicate ? `${fmtNum(s.duplicate)} טלפון כפול` : null, s.abroad ? `${fmtNum(s.abroad)} מספר חו״ל` : null].filter(Boolean) : [];
  return (
    <Dialog
      open={Boolean(kind)}
      onClose={onClose}
      size="md"
      icon={kind === "tzintuk" ? "bell" : "mic"}
      title={`${kind ? KIND_LABEL[kind] : ""} בימות — ${fmtNum(ids.length)} לידים`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" icon="phone" disabled={busy || !preview?.count || tooMany} onClick={() => void submit()}>
            {busy ? "משגר…" : `שיגור ל־${fmtNum(preview?.count ?? 0)} נמענים`}
          </Button>
        </>
      }
    >
      <div className="sl-stack">
        <p className="sl-note">
          {kind === "tzintuk"
            ? "צינתוק: הטלפון של הנמען מצלצל פעם אחת מהמספר המציג של הקו, בלי מענה. מתאים לתזכורת לחזור אליכם."
            : "הודעה קולית: הקו מחייג לכל נמען ומקריא את הטקסט (TTS) מתיקיית השיגור ‎/send-play‎ בקו."}
        </p>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {!preview && !error ? (
          <div className="sl-busy">
            <Spinner size={22} />
          </div>
        ) : null}
        {preview ? (
          <>
            <dl className="sl-kv">
              <div>
                <dt>נמענים</dt>
                <dd>
                  <b>{fmtNum(preview.count)}</b>
                </dd>
              </div>
              <div>
                <dt>לא ישוגרו</dt>
                <dd>{left.length ? left.join(" · ") : "—"}</dd>
              </div>
              <div>
                <dt>מספר מציג</dt>
                <dd className="sl-ltr">{preview.callerId || "—"}</dd>
              </div>
              <div>
                <dt>יתרת יחידות בקו</dt>
                <dd className="sl-num">{preview.units === null ? "—" : fmtNum(preview.units)}</dd>
              </div>
            </dl>
            {tooMany ? <Banner tone="danger">{`יותר מדי נמענים (${fmtNum(preview.count)}). המקסימום הוא ${fmtNum(preview.max)}.`}</Banner> : null}
            {!preview.count ? <Banner tone="warning">אין בבחירה לידים עם מספר טלפון בישראל.</Banner> : null}
            <Field label="כותרת" hint="להיסטוריית השיגורים (רשות)">
              <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === "tzintuk" ? "צינתוק ללידים" : "הודעה קולית ללידים"} />
            </Field>
            {kind === "voice" ? (
              <Field label="טקסט להקראה" full hint="אותו טקסט לכל הנמענים">
                <TextArea rows={4} value={text} onChange={(e) => setText(e.target.value)} />
              </Field>
            ) : null}
            {preview.sample.length ? (
              <SimpleTable
                ariaLabel="נמענים לדוגמה"
                rows={preview.sample}
                rowKey="phone"
                maxHeight={200}
                columns={[
                  { key: "name", label: "שם", render: (r: { name: string | null }) => r.name || "—" },
                  { key: "phone", label: "טלפון", ltr: true },
                ]}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

type CallLead = { id: string; name: string | null; phone: string | null };

/**
 * click-to-call (CreateBridgeCall): the user's phone rings first, then Yemot dials the lead.
 * With a saved phone it dials at once (Turbo's «התקשר»); otherwise it asks for the phone once and can keep it.
 */
export function useYemotCall(onCalled?: () => void) {
  const toast = useCkToast();
  const { setup, reload } = useYemotSetup();
  const [asking, setAsking] = useState<CallLead | null>(null);
  const [phone, setPhone] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const dial = async (lead: CallLead, fromPhone?: string) => {
    setBusy(true);
    try {
      const r = await yemotOutboundApi.call({ leadId: lead.id, fromPhone: fromPhone || null, remember: fromPhone ? remember : undefined });
      toast(r.message, { tone: "success", duration: 8000 });
      setAsking(null);
      if (fromPhone && remember) reload();
      onCalled?.();
    } catch (e) {
      toast.error(errorMessage(e, "החיוג נכשל"));
    } finally {
      setBusy(false);
    }
  };

  const start = (lead: CallLead, opts: { askPhone?: boolean } = {}) => {
    if (!lead.phone) {
      toast.error("אין לליד מספר טלפון");
      return;
    }
    if (setup?.myPhone && !opts.askPhone) {
      void dial(lead);
      return;
    }
    setPhone(setup?.myPhone ?? "");
    setRemember(true);
    setAsking(lead);
  };

  const dialog = (
    <Dialog
      open={Boolean(asking)}
      onClose={() => setAsking(null)}
      size="sm"
      icon="phone"
      title={`חיוג דרך ימות${asking?.name ? ` — ${asking.name}` : ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={() => setAsking(null)}>
            ביטול
          </Button>
          <Button variant="primary" icon="phone" disabled={busy || !phone.trim()} onClick={() => asking && void dial(asking, phone.trim())}>
            {busy ? "מחייג…" : "חיוג"}
          </Button>
        </>
      }
    >
      <div className="sl-stack">
        {setup && !setup.configured ? <Banner tone="warning">לא הוגדר חיבור לימות המשיח — חברו אותו בהגדרות המכירות.</Banner> : null}
        <p className="sl-note">
          הטלפון שלך יצלצל קודם. כשתענה, הקו יחייג אל <b className="sl-ltr">{asking?.phone}</b> ויחבר ביניכם.
        </p>
        <Field label="הטלפון שלך">
          <TextInput ltr type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0501234567" autoFocus />
        </Field>
        <Checkbox checked={remember} onChange={setRemember} label="לשמור כטלפון שלי לחיוגים הבאים" />
      </div>
    </Dialog>
  );

  return { start, dialog, setup, busy };
}
